import {
  UserDto,
  EnterpriseUserDto,
  PasswordSettingsDto,
  SignUpAuthModeSettingsDto,
  HrEmployeeOtpDto,
  TwoFactorDto,
  VerifyTwoFactorDto,
  UserSessionDto,
  VerifySignInDto,
  VerifyTwoFactorUserIdDto,
  UpdatePasswordDto,
  LoginTimeStampDto,
  IncreaseFailedAttemptsCountDto,
  PortalIssueTypeDto,
  PortalRequestFormDetailsDto,
  OtpResponseDto,
  PortalAutheticatorAppDto,
  TwoFactorResultDto,
  UserResponseDto,
  UserImpersonationRequestDto,
  UserImpersonationDto,
  EndImpersonationRequestDto,
  UserProfileDto,
  PasswordInfoDto,
  ResetPasswordDto,
  VerifyCurrentPasswordDto,
  VerifyCurrentPasswordResultDto,
  SaveProfileDto,
  SaveNFDto,
} from "../types/User";
import apiClient from "../utils/apiClient";
import { GLOBALSETTINGS } from "../data/constants";
import { GLOBALMESSAGES } from "../constants/messages";

export const createUser = async (userData?: UserDto): Promise<UserResponseDto> => {
  const response = await apiClient.post<UserResponseDto>("/PortalUser/user", userData);
  return response.data;
};
export const createAndSendOtp = async (
  employeeData?: HrEmployeeOtpDto
): Promise<OtpResponseDto> => {
  const response = await apiClient.post<OtpResponseDto>("/PortalUser/send-otp", employeeData);
  return response.data;
};

export const verifyOtp = async (employeeData?: HrEmployeeOtpDto): Promise<OtpResponseDto> => {
  const response = await apiClient.post<OtpResponseDto>("/PortalUser/verify-otp", employeeData);
  return response.data;
};
export const checkUsernameExist = async (username: string): Promise<boolean> => {
  const response = await apiClient.get<boolean>("/PortalUser/username-exist", {
    params: { username },
  });
  return response.data;
};
export const checkEmailOrPhoneExist = async (emailOrPhone: string): Promise<boolean> => {
  const response = await apiClient.get<boolean>("/PortalUser/email-or-phone-exist", {
    params: { emailOrPhone },
  });
  return response.data;
};

export const userExist = async (emailOrPhone: string): Promise<EnterpriseUserDto> => {
  const response = await apiClient.get<EnterpriseUserDto>("/PortalUser/details", {
    params: { emailOrPhone },
  });
  return response.data;
};

export const getPasswordSettings = async (): Promise<PasswordSettingsDto> => {
  const response = await apiClient.get<PasswordSettingsDto>("/PortalUser/password-settings");
  return response.data;
};
export const getSignUpAuthModeSettings = async (): Promise<SignUpAuthModeSettingsDto> => {
  const response = await apiClient.get<SignUpAuthModeSettingsDto>("/PortalUser/signup-auth-mode");
  return response.data;
};

export const generateQrCode = async (userEmail: string): Promise<TwoFactorDto> => {
  const response = await apiClient.get<TwoFactorDto>("/PortalUser/generate-qr-code", {
    params: { userEmail },
  });
  return response.data;
};
export const verify2FACode = async (verifyData: VerifyTwoFactorDto): Promise<boolean> => {
  const key = verifyData.secretKey;
  const code = verifyData?.code;
  const email = verifyData?.email;
  const response = await apiClient.get<boolean>("/PortalUser/verify-tfa-code", {
    params: { key, code, email },
  });
  return response.data;
};

export const getIpAddress = async (): Promise<string> => {
  // cache-busting: the browser can otherwise serve a stale IP after a network switch,
  // which would defeat login-time IP re-evaluation.
  // `fetch` has no default timeout, and this is a third-party host sitting on the login
  // critical path — an unreachable ipify held the whole sign-in open indefinitely. Bounded
  // with an AbortController; both callers already treat a failed lookup as non-fatal
  // (SignIn skips the IP refresh when it is falsy, errorLogger catches to "").
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), GLOBALSETTINGS.IP_LOOKUP_TIMEOUT_MS);
  try {
    const response = await fetch(`https://api.ipify.org?format=json&_=${Date.now()}`, {
      cache: "no-store",
      signal: controller.signal,
    });
    const data = await response.json();
    return data.ip;
  } catch (error) {
    // An abort rejects with a raw DOMException ("signal is aborted without reason"), which
    // useApi would put straight into a toast and the error log. Translate it to something a
    // user can read; genuine failures still propagate untouched.
    if (controller.signal.aborted) {
      throw new Error(GLOBALMESSAGES.IP_LOOKUP_TIMEOUT);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};
export const createUserSession = async (userSessionData: UserSessionDto): Promise<string> => {
  const response = await apiClient.post<string>("/PortalUser/session", userSessionData);
  return response.data;
};
// Refreshes only the current session's IP (used at login time to re-evaluate the client IP
// against the whitelist after a possible network change since page load).
export const refreshSessionIp = async (payload: {
  sessionValue: string;
  IpAddress: string;
}): Promise<number> => {
  const response = await apiClient.put<number>("/PortalUser/session-ip", payload);
  return response.data;
};
export const increaseFailedLoginAttempts = async (sessionId: number): Promise<boolean> => {
  const response = await apiClient.put<boolean>(
    "/PortalUser/session-increase-failed-attempts",
    sessionId
  );
  return response.data;
};

export const verifyUserSignIn = async (
  verifySignInData: VerifySignInDto
): Promise<VerifySignInDto> => {
  const response = await apiClient.put<VerifySignInDto>(
    "/PortalUser/verify-sign-in",
    verifySignInData
  );
  return response.data;
};

export const verify2FACodeByUserId = async (
  verifyData: VerifyTwoFactorUserIdDto
): Promise<TwoFactorResultDto> => {
  const userId = verifyData.userId;
  const code = verifyData?.code;
  const response = await apiClient.get<TwoFactorResultDto>(
    "/PortalUser/verify-tfa-code-by-userid",
    {
      params: { userId, code },
    }
  );
  return response.data;
};
export const updatePassword = async (verifySignInRequest: UpdatePasswordDto): Promise<string> => {
  const response = await apiClient.put<string>("/PortalUser/update-password", verifySignInRequest);
  return response.data;
};

export const updateLoginTimeStamp = async (loginTimeStamp: LoginTimeStampDto): Promise<boolean> => {
  const response = await apiClient.put<boolean>(
    "/PortalUser/update-login-timestamp",
    loginTimeStamp
  );
  return response.data;
};
export const updateLogoutTimeStamp = async (sessionValue: string): Promise<boolean> => {
  const response = await apiClient.put<boolean>("/PortalUser/update-logout-timestamp", null, {
    params: { sessionValue },
  });
  return response.data;
};
export const increaseFailedAttemptsCount = async (
  failedAttemptsData: IncreaseFailedAttemptsCountDto
): Promise<string> => {
  const response = await apiClient.put<string>(
    "/PortalUser/increase-failed-attempts-count",
    failedAttemptsData
  );
  return response.data;
};
export const getPortalIssueType = async (): Promise<PortalIssueTypeDto[]> => {
  const response = await apiClient.get<PortalIssueTypeDto[]>(`/PortalUser/request-form-issue-type`);
  return response.data;
};
export const sendHelpDeskRequestForm = async (
  portalRequestFormDetailsData: PortalRequestFormDetailsDto
): Promise<boolean> => {
  const response = await apiClient.post<boolean>(
    `/PortalUser/help-desk-request-form`,
    portalRequestFormDetailsData
  );
  return response.data;
};

export const checkIsSessionLocked = async (sessionValue: string): Promise<boolean> => {
  const response = await apiClient.get<boolean>("/PortalUser/is-session-locked", {
    params: { sessionValue },
  });
  return response.data;
};

export const addSecretKey = async (
  verifySignInData: PortalAutheticatorAppDto
): Promise<boolean> => {
  const response = await apiClient.post<boolean>("/PortalUser/secret-key", verifySignInData);
  return response.data;
};

export const impersonateUserDetails = async (
  userImpersonationDto: UserImpersonationRequestDto
): Promise<UserImpersonationDto | null> => {
  const response = await apiClient.post<UserImpersonationDto | null>(
    "/PortalUser/impersonate-user",
    userImpersonationDto
  );
  return response.data;
};

export const endImpersonateUser = async (
  endImpersonationRequestDto: EndImpersonationRequestDto
): Promise<boolean> => {
  const response = await apiClient.post<boolean>(
    `/PortalUser/end-impersonate-user/${endImpersonationRequestDto.impersonationId}/${endImpersonationRequestDto.impersonatorUserId}`
  );
  return response.data;
};
type CaptchaResponse = { success: boolean };
export const VerifyCaptcha = async (token: string): Promise<boolean> => {
  const response = await apiClient.post<CaptchaResponse>("/PortalUser/verify-captcha", { token });

  return response.data.success;
};
export const getUserProfileInfo = async (userId: number ): Promise<UserProfileDto> => {
  const response = await apiClient.get<UserProfileDto>(`/UserProfile/userwithroles-details-by-userId/${userId}`);
  return response.data;
};

export const getAccountInfo = async (userId: number ): Promise<PasswordInfoDto> => {
  const response = await apiClient.get<PasswordInfoDto>(`/UserProfile/account-details-by-userId/${userId}`);
  return response.data;
};

export const resetPassword = async (verifySignInRequest: ResetPasswordDto): Promise<string> => {
  const response = await apiClient.put<string>("/UserProfile/reset-password", verifySignInRequest);
  return response.data;
};
export const verifyCurrentPassword = async (
  verifyRequest: VerifyCurrentPasswordDto,
): Promise<VerifyCurrentPasswordResultDto> => {
  const response = await apiClient.post<VerifyCurrentPasswordResultDto>(
    "/UserProfile/verify-current-password",
    verifyRequest,
  );
  return response.data;
};
export const saveUserProfileInfo = async (UserProfileInfo: SaveProfileDto): Promise<string> => {
  const response = await apiClient.put<string>("/UserProfile/updateuserprofile-byuserId", UserProfileInfo);
  return response.data;
};
export const saveNotificationPreferences = async (NotificationPreferences: SaveNFDto): Promise<string> => {
  const response = await apiClient.put<string>("/UserProfile/updatenotificationpreference-byuserId", NotificationPreferences);
  return response.data;
};
export const saveProfilePhoto = async (formData: FormData): Promise<string> => {
  const response = await apiClient.post<string>('/UserProfile/profilePhoto', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};
export const removeProfilePhoto = async (userId: number): Promise<boolean> => {
  const response = await apiClient.delete<boolean>(`/UserProfile/profilePhoto/${userId}`);
  return response.data;
};
export const getEmployeeProfileImage = async (userId: number ): Promise<string> => {
  const response = await apiClient.get<string>(`/UserProfile/employee-profile-image/${userId}`);
  return response.data;
};
